import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, MessageSquareReply } from 'lucide-react'
import { DM_KEYWORD_MAX, DM_TEMPLATE_MAX } from '@dealcloser/shared'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { BetaBadge } from '@/components/beta-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSetDmTrigger } from './use-content'
import type { Post } from './types'

/**
 * Per-post comment keyword trigger ("Comment GUIDE below and I'll send it
 * to you"). People whose comment contains the keyword get this message by
 * LinkedIn DM, once each, if they are 1st-degree connections and the
 * Comment-to-DM switch in Settings > Safety is on. See
 * docs/content/engagement-to-dm.md.
 */
export function DmTriggerDialog({ post, onClose }: { readonly post: Post | null; readonly onClose: () => void }) {
  const [keyword, setKeyword] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { mutateAsync: save, isPending } = useSetDmTrigger()

  useEffect(() => {
    if (!post) return
    setKeyword(post.dm_keyword ?? '')
    setMessage(post.dm_template ?? 'Hi {first_name}, thanks for commenting. Here it is: ')
    setError(null)
  }, [post])

  async function handleSave(clear = false) {
    if (!post) return
    setError(null)
    try {
      await save({ id: post.id, keyword: clear ? null : keyword.trim() || null, message: clear ? undefined : message })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the comment trigger.')
    }
  }

  return (
    <Dialog open={post !== null} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareReply className="h-5 w-5 text-cobalt-bright" />
            Comment keyword DM
            <BetaBadge />
          </DialogTitle>
          <DialogDescription>
            Ask readers to comment a word (for example &quot;Comment GUIDE below&quot;). Everyone whose comment
            contains it gets the message below as a LinkedIn DM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="dm-keyword">Keyword</Label>
            <Input
              id="dm-keyword"
              placeholder="GUIDE"
              value={keyword}
              maxLength={DM_KEYWORD_MAX}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={isPending}
            />
            <p className="text-[11px] text-muted-foreground">
              Matches the whole word in any case, so GUIDE matches &quot;guide please&quot; but not &quot;guidelines&quot;.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dm-message">Message they get</Label>
            <textarea
              id="dm-message"
              className="min-h-[110px] w-full resize-y rounded-md border bg-transparent p-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              value={message}
              maxLength={DM_TEMPLATE_MAX}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isPending}
            />
            <p className="text-[11px] text-muted-foreground">
              Sent exactly as written. {'{first_name}'} becomes their first name. Include the link or resource you promised.
            </p>
          </div>

          <ul className="list-disc space-y-1 rounded-md border bg-muted/30 py-2 pl-7 pr-3 text-[11px] text-muted-foreground">
            <li>
              Only sends while Comment-to-DM is on in{' '}
              <Link to="/settings?tab=safety" className="text-cobalt-bright hover:underline">Settings, Safety</Link> (off by default).
            </li>
            <li>LinkedIn only allows messages to 1st-degree connections. Commenters you are not connected with are skipped.</li>
            <li>One message per person per post, up to 10 comment DMs a day, on weekdays from 9 AM to 5 PM New York time.</li>
            <li>On this post, likes and comments without the keyword get no message.</li>
            <li>With &quot;Approve every message&quot; review mode, each one waits in your Inbox for approval.</li>
          </ul>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <div className="flex items-center justify-between gap-2">
            {post?.dm_keyword ? (
              <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={() => void handleSave(true)} disabled={isPending}>
                Remove trigger
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
              <Button onClick={() => void handleSave()} disabled={isPending || !keyword.trim() || !message.trim()} className="gap-2">
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
